import React, { useState, useRef, useMemo, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { makeStyles } from '@mui/styles';
import { Typography, Box, Paper, LinearProgress, Fade, CircularProgress } from '@mui/material';
import { CloudUpload, Crop, Image as ImageIcon } from '@mui/icons-material';
import Cropper from 'react-cropper';
import '../../../../cropper-styles.css';
import { useDispatch } from 'react-redux';
import { uploadMenuPhoto } from 'store/actions/restaurantMenu';

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: theme.spacing(2),
      minWidth: '600px',
      maxWidth: '800px',
      minHeight: '500px',
    }
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2, 3),
    '& .MuiTypography-h6': {
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    }
  },
  dialogContent: {
    padding: theme.spacing(3),
    minHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3)
  },
  uploadSection: {
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    textAlign: 'center',
    backgroundColor: theme.palette.grey[50],
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light + '10',
    },
    '&.dragover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light + '20',
    }
  },
  uploadIcon: {
    fontSize: '48px',
    color: theme.palette.grey[400],
    marginBottom: theme.spacing(1)
  },
  uploadText: {
    color: theme.palette.grey[600],
    marginBottom: theme.spacing(2)
  },
  hiddenInput: {
    display: 'none'
  },
  uploadButton: {
    borderRadius: theme.spacing(3),
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontWeight: 600
  },
  cropSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  cropContainer: {
    position: 'relative',
    width: '100%',
    height: '400px',
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    '& .cropper-container': {
      height: '100% !important'
    }
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100],
    zIndex: 1000
  },
  cropInstructions: {
    backgroundColor: theme.palette.info.light + '20',
    borderLeft: `4px solid ${theme.palette.info.main}`,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(0, 1, 1, 0)
  },
  previewSection: {
    textAlign: 'center'
  },
  fileName: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginTop: theme.spacing(1)
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
    backgroundColor: theme.palette.grey[50]
  },
  actionButton: {
    borderRadius: theme.spacing(3),
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontWeight: 600,
    minWidth: '100px'
  },
  cancelButton: {
    color: theme.palette.grey[600],
    '&:hover': {
      backgroundColor: theme.palette.grey[100]
    }
  },
  uploadActionButton: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.success.dark
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.grey[500]
    }
  },
  progressSection: {
    marginTop: theme.spacing(2)
  }
}));

const MenuItemImage = (props) => {
    const { open, menuId, categoryId, menuItemId } = props;
    const dispatch = useDispatch();
    const classes = useStyles();
    const cropperRef = useRef(null);
    const uploadPhoto = (values) => dispatch(uploadMenuPhoto(values));

    const [croppedImageUrl, setCroppedImageUrl] = useState(null);    
    const [image, setImage] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [content, setContent] = useState(null);
    const [cropData, setCropData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [cropperReady, setCropperReady] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);

    // Memoize the image URL to prevent recreation on every render
    const imageUrl = useMemo(() => {
        if (image) {
            return URL.createObjectURL(image);
        }
        return null;
    }, [image]);

    // Cleanup image URL when component unmounts or image changes
    useEffect(() => {
        return () => {
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
        };
    }, [imageUrl]);

    // Reset cropper ready state when image changes
    useEffect(() => {
        if (image) {
            setCropperReady(false);
            setIsLoading(true);
        }
    }, [image]);

    const handleClose = () => {
        // Cleanup image URL
        if (imageUrl) {
            URL.revokeObjectURL(imageUrl);
        }
        
        // Reset all state when closing
        setImage(null);
        setCroppedImage(null);
        setContent(null);
        setCropData(null);
        setCroppedImageUrl(null);
        setIsLoading(false);
        setCropperReady(false);
        setUploadLoading(false);
        props.handImageClose();
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            processImageFile(file);
        }
    };

    const processImageFile = (file) => {
        // Check if file is an image
        if (file && file.type.startsWith('image/')) {
            setImage(file);
            // Reset crop state when new image is selected
            setCropData(null);
            setCroppedImage(null);
            setCropperReady(false);
            setIsLoading(true);
            setUploadLoading(false);
        } else {
            alert('Please select a valid image file (JPG, PNG, etc.)');
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.classList.add('dragover');
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.classList.remove('dragover');
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.classList.remove('dragover');
        
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            processImageFile(files[0]);
        }
    };

    const uploadThePhoto = async () => {
        if (!image || !croppedImage) {
            console.error('No images');
            return;
        }

        setUploadLoading(true);
        
        try {
            let form_data = new FormData();
            form_data.append('image', croppedImage);
            form_data.append('content', content);
    	
            const data = {
                menuId, categoryId, menuItemId, form_data
            };
            
            await uploadPhoto(data);
            
            // Close dialog on successful upload
            setTimeout(() => {
                handleClose();
            }, 1000);
            
        } catch (error) {
            console.error('Upload failed:', error);
            setUploadLoading(false);
        }
    };

    const onCropperReady = () => {
        setCropperReady(true);
        setIsLoading(false);
        // Get initial crop data immediately
        getCropData();
        // Also get it after a short delay as a backup
        setTimeout(() => {
            getCropData();
        }, 100);
    };

    const getCropData = () => {
        // console.log('getCropData called', { 
        //     cropperExists: !!cropperRef.current?.cropper, 
        //     cropperReady 
        // });
        
        if (cropperRef.current?.cropper) {
            try {
                const canvas = cropperRef.current.cropper.getCroppedCanvas({
                    width: 400,
                    height: 400,
                    imageSmoothingEnabled: true,
                    imageSmoothingQuality: 'high',
                });
                
                if (canvas) {
                    // console.log('Canvas created successfully');
                    // Set crop data immediately from canvas
                    const dataURL = canvas.toDataURL('image/jpeg', 0.95);
                    setCropData(dataURL);
                    
                    // Create the blob for file upload
                    canvas.toBlob(blob => {
                        if (blob) {
                            // console.log('Blob created successfully');
                            const reader = new FileReader();
                            reader.readAsDataURL(blob);
                            reader.onloadend = () => {
                                dataURLtoFile(reader.result, 'cropped.jpg');
                            };
                            
                            // Create preview URL
                            const croppedImageUrl = URL.createObjectURL(blob);
                            setCroppedImageUrl(croppedImageUrl);
                        }
                    }, 'image/jpeg', 0.95);
                } else {
                    console.warn('Canvas could not be created from cropper');
                }
            } catch (error) {
                console.error('Error getting crop data:', error);
            }
        } else {
            console.warn('Cropper not available');
        }
    };

    const dataURLtoFile = (dataurl, filename) => {
        let arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]), 
            n = bstr.length, 
            u8arr = new Uint8Array(n);
                
        while(n--){
            u8arr[n] = bstr.charCodeAt(n);
        }
        let croppedImg = new File([u8arr], filename, {type:mime});
        setCroppedImage(croppedImg); 
    };

    const isUploadReady = cropData !== null && cropperReady;
    
    // Debug logging
    // useEffect(() => {
    //     console.log('Upload ready state:', {
    //         cropData: cropData !== null,
    //         cropperReady,
    //         croppedImage: croppedImage !== null,
    //         uploadLoading,
    //         isUploadReady
    //     });
    // }, [cropData, cropperReady, croppedImage, uploadLoading, isUploadReady]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            aria-labelledby="form-dialog-title"
            className={classes.dialog}
            maxWidth={false}
        >
            <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
                <ImageIcon />
                Upload Dish Image
            </DialogTitle>
            
            <DialogContent className={classes.dialogContent}>
                {!image ? (
                    <Paper 
                        className={classes.uploadSection} 
                        onClick={() => document.getElementById('image-upload').click()}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <CloudUpload className={classes.uploadIcon} />
                        <Typography variant="h6" className={classes.uploadText}>
                            Click to upload or drag and drop
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Supports JPG, PNG files
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            className={classes.uploadButton}
                            startIcon={<CloudUpload />}
                        >
                            Choose File
                        </Button>
                        <input
                            id="image-upload"
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className={classes.hiddenInput}
                        />
                    </Paper>
                ) : (
                    <div className={classes.cropSection}>
                        <Box className={classes.cropInstructions}>
                            <Typography variant="body2" display="flex" alignItems="center" gap={1}>
                                <Crop fontSize="small" />
                                Drag corners to resize, drag inside to move the crop area. Use mouse wheel to zoom. The image will be cropped to a square format.
                            </Typography>
                        </Box>
                        
                        <div className={classes.cropContainer}>
                            {isLoading && (
                                <div className={classes.loadingContainer}>
                                    <CircularProgress />
                                </div>
                            )}
                            {imageUrl && (
                                <Cropper
                                    ref={cropperRef}
                                    src={imageUrl}
                                    style={{ 
                                        height: '100%', 
                                        width: '100%',
                                        opacity: cropperReady ? 1 : 0,
                                        transition: 'opacity 0.3s ease'
                                    }}
                                    initialAspectRatio={1}
                                    aspectRatio={1}
                                    guides={true}
                                    cropend={getCropData}
                                    ready={onCropperReady}
                                    zoom={getCropData}
                                    cropmove={getCropData}
                                    cropBoxMovable={true}
                                    cropBoxResizable={true}
                                    dragMode="move"
                                    viewMode={0}
                                    minCropBoxHeight={20}
                                    minCropBoxWidth={20}
                                    background={true}
                                    responsive={true}
                                    autoCropArea={0.6}
                                    checkOrientation={false}
                                    scalable={true}
                                    zoomable={true}
                                    wheelZoomRatio={0.2}
                                    minZoom={0.01}
                                    maxZoom={5}
                                    zoomOnTouch={true}
                                    zoomOnWheel={true}
                                />
                            )}
                        </div>
                        
                        <div className={classes.previewSection}>
                            <Typography variant="body2" color="textSecondary">
                                Selected file:
                            </Typography>
                            <Typography variant="body2" className={classes.fileName}>
                                {image.name}
                            </Typography>
                            
                            {uploadLoading && (
                                <div className={classes.progressSection}>
                                    <Typography variant="body2" color="primary" style={{ marginTop: 16, marginBottom: 8 }}>
                                        Uploading image...
                                    </Typography>
                                    <LinearProgress color="primary" />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
            
            <DialogActions className={classes.dialogActions}>
                <Button 
                    onClick={handleClose} 
                    className={`${classes.actionButton} ${classes.cancelButton}`}
                >
                    Cancel
                </Button>
                
                {image && (
                    <Button 
                        onClick={() => {
                            if (imageUrl) {
                                URL.revokeObjectURL(imageUrl);
                            }
                            setImage(null);
                            setCropData(null);
                            setCroppedImage(null);
                            setCropperReady(false);
                            setIsLoading(false);
                            setUploadLoading(false);
                        }}
                        className={`${classes.actionButton} ${classes.cancelButton}`}
                    >
                        Choose Different
                    </Button>
                )}
                
                <Button 
                    onClick={uploadThePhoto} 
                    disabled={!isUploadReady || uploadLoading}
                    className={`${classes.actionButton} ${classes.uploadActionButton}`}
                    startIcon={uploadLoading ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}
                >
                    {uploadLoading ? 'Uploading...' : 'Upload Image'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MenuItemImage;